import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import { NativeSelect } from '../components/common/FormField';
import { Spinner } from '../components/common/PageHeader';
import { displayStatus } from '../components/billing/invoiceStatus';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage, triggerBlobDownload } from '../services/api';
import { BillingInvoice, InvoiceStatus } from '../types';
import { amountInWordsInr } from '../utils/amountInWords';
import { fmtCurrency, fmtDate, fmtNum, PAYMENT_MODES, paymentModeLabel, todayISO, toNum } from '../utils/format';
import { ACK_NO_PATTERN, compact, hasErrors, IRN_PATTERN, v } from '../utils/validation';

// Rubber-stamp style status mark shown on the invoice document
const STATUS_STAMP: Record<InvoiceStatus | 'overdue', { classes: string; label: string }> = {
  draft: { classes: 'border-ink-400 text-ink-500', label: 'Draft' },
  sent: { classes: 'border-sky-500 text-sky-600', label: 'Sent' },
  paid: { classes: 'border-green-600 text-green-600', label: 'Paid' },
  cancelled: { classes: 'border-red-500 text-red-600', label: 'Cancelled' },
  overdue: { classes: 'border-accent-600 text-accent-600', label: 'Overdue' },
};

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const invoiceId = id ? parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission('billing:update');
  const canDownload = hasPermission('billing:download');

  const [downloading, setDownloading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [payment, setPayment] = useState({ paidAt: todayISO(), paymentMode: 'bank_transfer', paymentReference: '', tdsAmount: '' });
  const [cancelReason, setCancelReason] = useState('');
  const [einvoice, setEinvoice] = useState({ irnNo: '', ackNo: '', ackDate: '' });
  const [paymentErrors, setPaymentErrors] = useState<Partial<Record<'paidAt' | 'paymentMode' | 'paymentReference' | 'tdsAmount', string>>>({});
  const [cancelError, setCancelError] = useState('');
  const [einvoiceErrors, setEinvoiceErrors] = useState<Partial<Record<'irnNo' | 'ackNo' | 'ackDate', string>>>({});

  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: queryKeys.billing.invoice(invoiceId),
    queryFn: () => api.get<BillingInvoice>(`/billing/invoices/${invoiceId}`),
    enabled: !isNaN(invoiceId),
  });

  useEffect(() => {
    if (invoice) setEinvoice({ irnNo: invoice.irnNo ?? '', ackNo: invoice.ackNo ?? '', ackDate: invoice.ackDate ?? '' });
  }, [invoice]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.billing.all() });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const statusMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/billing/invoices/${invoiceId}/status`, body),
    onSuccess: refresh,
  });
  const einvoiceMutation = useMutation({
    mutationFn: () => api.patch(`/billing/invoices/${invoiceId}/einvoice`, einvoice),
    onSuccess: refresh,
  });

  async function transition(body: Record<string, unknown>, successMsg: string): Promise<boolean> {
    try {
      await statusMutation.mutateAsync(body);
      showToast(successMsg, 'success');
      return true;
    } catch (err) {
      showToast(errorMessage(err, 'Failed to update status'), 'error');
      return false;
    }
  }

  async function recordPayment() {
    if (!invoice) return;
    const total = parseFloat(invoice.totalAmount);
    const e = compact({
      paidAt: v.date(payment.paidAt, 'Payment date', { notFuture: true, min: invoice.invoiceDate, minLabel: `the invoice date (${fmtDate(invoice.invoiceDate)})` }),
      paymentMode: payment.paymentMode ? undefined : 'Payment mode is required',
      paymentReference: v.reference(payment.paymentReference),
      tdsAmount:
        v.money(payment.tdsAmount, 'TDS', { required: false, allowZero: true }) ??
        ((toNum(payment.tdsAmount) ?? 0) >= total ? 'TDS must be less than the invoice total' : undefined),
    });
    setPaymentErrors(e);
    if (hasErrors(e)) return;
    const ok = await transition(
      {
        status: 'paid',
        paidAt: payment.paidAt,
        paymentMode: payment.paymentMode,
        paymentReference: payment.paymentReference.trim() || undefined,
        tdsAmount: toNum(payment.tdsAmount) ?? 0,
      },
      'Payment recorded — invoice marked as paid',
    );
    if (ok) setShowPayment(false);
  }

  async function cancelInvoice() {
    const reasonError = v.text(cancelReason, 'Reason', { required: true, min: 5, max: 255 });
    setCancelError(reasonError ?? '');
    if (reasonError) return;
    const ok = await transition({ status: 'cancelled', cancelReason: cancelReason.trim() }, 'Invoice cancelled');
    if (ok) setShowCancel(false);
  }

  async function downloadPdf() {
    if (!invoice) return;
    setDownloading(true);
    try {
      const blob = await api.download(`/billing/invoices/${invoice.id}/pdf`);
      triggerBlobDownload(blob, `${invoice.invoiceNumber.replace(/\//g, '-')}.pdf`);
    } catch (err) {
      showToast(errorMessage(err, 'Failed to download invoice'), 'error');
    } finally {
      setDownloading(false);
    }
  }

  async function saveEinvoice() {
    if (!invoice) return;
    const irn = einvoice.irnNo.trim();
    const ack = einvoice.ackNo.trim();
    const e = compact({
      irnNo: irn && !IRN_PATTERN.test(irn) ? `IRN must be the 64-character code from the e-invoice portal (you entered ${irn.length})` : undefined,
      ackNo: ack && !ACK_NO_PATTERN.test(ack) ? 'ACK number must be 6-20 digits' : !ack && irn ? 'ACK number is required with an IRN' : undefined,
      ackDate:
        v.date(einvoice.ackDate, 'ACK date', { required: false, notFuture: true, min: invoice.invoiceDate, minLabel: 'the invoice date' }) ??
        (!einvoice.ackDate && irn ? 'ACK date is required with an IRN' : undefined),
    });
    setEinvoiceErrors(e);
    if (hasErrors(e)) return;
    try {
      await einvoiceMutation.mutateAsync();
      showToast('E-invoice details saved', 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to save e-invoice details'), 'error');
    }
  }

  if (isLoading) {
    return <Layout><Spinner /></Layout>;
  }

  if (isError || !invoice) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-red-600">Invoice not found.</p>
          <Button variant="outline" onClick={() => navigate('/billing')} className="mt-4">Back to Billing</Button>
        </div>
      </Layout>
    );
  }

  const stamp = STATUS_STAMP[displayStatus(invoice)];
  const subtotal = parseFloat(invoice.subtotal);
  const cgstAmount = parseFloat(invoice.cgstAmount);
  const sgstAmount = parseFloat(invoice.sgstAmount);
  const igstAmount = parseFloat(invoice.igstAmount);
  const roundOff = parseFloat(invoice.roundOff);
  const total = parseFloat(invoice.totalAmount);
  const tds = parseFloat(invoice.tdsAmount);
  const issuer = invoice.issuer;
  const billTo = invoice.billTo;
  const busy = statusMutation.isPending;
  const lineItems = invoice.lineItems ?? [];
  const einvoiceLocked = !canUpdate || invoice.status === 'cancelled';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Top bar: back nav + actions */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4 lg:mb-6">
          <button className="flex items-center gap-2 text-sm text-ink-500 hover:text-ink-800 self-start" onClick={() => navigate('/billing')}>
            <i className="fas fa-arrow-left" />
            Back to Billing
          </button>

          <div className="flex flex-col w-full gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:w-auto">
            {canDownload && (
              <Button variant="outline" size="sm" icon="fa-download" disabled={downloading} onClick={downloadPdf}>
                {downloading ? 'Downloading…' : 'Download PDF'}
              </Button>
            )}
            {canUpdate && invoice.status === 'draft' && (
              <Button variant="info" size="sm" icon="fa-paper-plane" disabled={busy}
                onClick={() => transition({ status: 'sent' }, 'Invoice marked as sent')}>
                Mark Sent
              </Button>
            )}
            {canUpdate && (invoice.status === 'draft' || invoice.status === 'sent') && (
              <>
                <Button variant="success" size="sm" icon="fa-check" disabled={busy}
                  onClick={() => { setPayment({ paidAt: todayISO(), paymentMode: 'bank_transfer', paymentReference: '', tdsAmount: '' }); setPaymentErrors({}); setShowPayment(true); }}>
                  Record Payment
                </Button>
                <Button variant="danger" size="sm" icon="fa-times" disabled={busy}
                  onClick={() => { setCancelReason(''); setCancelError(''); setShowCancel(true); }}>
                  Cancel Invoice
                </Button>
              </>
            )}
            {canUpdate && invoice.status === 'sent' && (
              <Button variant="secondary" size="sm" icon="fa-rotate-left" disabled={busy}
                onClick={() => transition({ status: 'draft' }, 'Invoice moved back to draft')}>
                Back to Draft
              </Button>
            )}
            {canUpdate && invoice.status === 'paid' && (
              <Button variant="secondary" size="sm" icon="fa-rotate-left" disabled={busy}
                onClick={() => transition({ status: 'sent' }, 'Payment undone — invoice is Sent again')}>
                Undo Payment
              </Button>
            )}
          </div>
        </div>

        {/* Invoice document */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-ink-300 mb-4">
          <div className="p-5 sm:p-8 lg:p-10">
            <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b-2 border-ink-900">
              <div>
                <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-ink-900 uppercase">Tax Invoice</h1>
                <p className="font-mono text-sm lg:text-base font-semibold text-ink-700 mt-2 break-all">{invoice.invoiceNumber}</p>
                <p className="text-sm font-semibold text-ink-800 mt-3">{issuer.companyName}</p>
                <p className="text-xs text-ink-500 max-w-sm">{issuer.address}</p>
                {issuer.gstNumber && <p className="text-xs text-ink-500">GSTIN: {issuer.gstNumber}</p>}
              </div>
              <span className={`inline-block border-2 rounded-md px-4 py-1.5 text-sm font-bold uppercase tracking-[0.25em] -rotate-3 ${stamp.classes}`}>
                {stamp.label}
              </span>
            </div>

            {/* Parties & dates */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-6">
              <div className="sm:col-span-2">
                <Label>Billed To</Label>
                <p className="text-sm font-semibold text-ink-900">{billTo.companyName}</p>
                {billTo.address && (
                  <p className="text-xs text-ink-500 mt-1">
                    {[billTo.address, [billTo.city, billTo.state].filter(Boolean).join(', '), billTo.pinCode].filter(Boolean).join(' - ')}
                  </p>
                )}
                {billTo.gstNumber && <p className="text-xs text-ink-500">GST No: {billTo.gstNumber}</p>}
                {billTo.contactPerson && <p className="text-xs text-ink-500">Attn: {billTo.contactPerson}</p>}
              </div>
              <div className="space-y-3">
                <Meta label="Invoice Date" value={fmtDate(invoice.invoiceDate)} />
                <Meta label="Due Date" value={fmtDate(invoice.dueDate)} danger={invoice.isOverdue} />
                {invoice.billingPeriodStart && (
                  <Meta label="Billing Period" value={`${fmtDate(invoice.billingPeriodStart)} – ${fmtDate(invoice.billingPeriodEnd)}`} />
                )}
                {invoice.status === 'paid' && invoice.paidAt && (
                  <div>
                    <Label>Paid On</Label>
                    <p className="text-sm font-semibold text-green-700">{fmtDate(invoice.paidAt)}</p>
                    <p className="text-xs text-ink-500">
                      {paymentModeLabel(invoice.paymentMode)}
                      {invoice.paymentReference ? ` · ${invoice.paymentReference}` : ''}
                    </p>
                    {tds > 0 && (
                      <p className="text-xs text-ink-500">
                        TDS {fmtCurrency(tds)} · Received {fmtCurrency(invoice.amountReceived)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b-2 border-ink-900">
                    <th className="text-left px-2 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide w-8">#</th>
                    <th className="text-left px-3 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Description</th>
                    <th className="text-left px-3 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">HSN/SAC</th>
                    <th className="text-right px-3 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Qty</th>
                    <th className="text-left px-3 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Unit</th>
                    <th className="text-right px-3 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Rate (₹)</th>
                    <th className="text-right px-3 py-3 text-xs font-bold text-ink-900 uppercase tracking-wide">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={item.id} className="border-b border-ink-200 hover:bg-ink-50 align-top">
                      <td className="px-2 py-3 text-ink-500">{idx + 1}</td>
                      <td className="px-3 py-3">
                        <span className="font-semibold text-ink-800">{item.title}</span>
                        {item.description && <span className="block text-xs italic text-ink-500 mt-0.5">{item.description}</span>}
                      </td>
                      <td className="px-3 py-3 font-mono text-ink-600">{item.hsnSac}</td>
                      <td className="px-3 py-3 text-right font-mono text-ink-700">{fmtNum(item.quantity)}</td>
                      <td className="px-3 py-3 text-ink-500">{item.unit}</td>
                      <td className="px-3 py-3 text-right font-mono text-ink-700">{fmtCurrency(item.rate).replace('₹', '')}</td>
                      <td className="px-3 py-3 text-right font-mono font-semibold text-ink-900">{fmtCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  {lineItems.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-8 text-center text-ink-400">No line items</td></tr>
                  )}
                </tbody>
                <tfoot>
                  <FootRow label="Subtotal" value={fmtCurrency(subtotal)} first />
                  {invoice.taxType === 'inter' ? (
                    <FootRow label={`IGST @ ${parseFloat(invoice.igstPercent)}%`} value={fmtCurrency(igstAmount)} />
                  ) : (
                    <>
                      <FootRow label={`CGST @ ${parseFloat(invoice.cgstPercent)}%`} value={fmtCurrency(cgstAmount)} />
                      <FootRow label={`SGST @ ${parseFloat(invoice.sgstPercent)}%`} value={fmtCurrency(sgstAmount)} />
                    </>
                  )}
                  {roundOff !== 0 && (
                    <FootRow label="ROUND OFF" value={roundOff < 0 ? `(-)${Math.abs(roundOff).toFixed(2)}` : roundOff.toFixed(2)} />
                  )}
                  <tr>
                    <td colSpan={6} className="px-3 py-3 text-right text-base font-bold uppercase tracking-wide text-ink-900">Gross Total</td>
                    <td className="px-3 py-3 text-right font-mono text-lg font-bold text-ink-900 whitespace-nowrap">{fmtCurrency(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="mt-4 text-sm font-semibold text-ink-800">{amountInWordsInr(total)}</p>

            {invoice.notes && (
              <Block title="Remark"><p className="text-sm text-ink-700">{invoice.notes}</p></Block>
            )}

            {invoice.status === 'cancelled' && (
              <Block title="Cancelled">
                <p className="text-sm text-red-700">{invoice.cancelReason || 'No reason recorded'}</p>
              </Block>
            )}

            {(issuer.bankName || issuer.bankAccountNo) && (
              <Block title="Company's Bank Details">
                {issuer.bankAccountName && <p className="text-sm text-ink-700">A/c Name: {issuer.bankAccountName}</p>}
                {issuer.bankName && <p className="text-sm text-ink-700">Bank Name: {issuer.bankName}</p>}
                {issuer.bankAccountNo && <p className="text-sm text-ink-700">A/c No.: {issuer.bankAccountNo}</p>}
                {(issuer.bankBranch || issuer.bankIfsc) && (
                  <p className="text-sm text-ink-700">Branch &amp; IFS Code: {[issuer.bankBranch, issuer.bankIfsc].filter(Boolean).join(' & ')}</p>
                )}
                {issuer.upiId && <p className="text-sm text-ink-700">UPI: {issuer.upiId}</p>}
              </Block>
            )}

            {/* GST e-invoice (IRN/ACK) */}
            <Block title="GST E-Invoice (IRN / ACK)">
              <p className="text-xs text-ink-500 mb-3">
                Generate the e-invoice separately on the govt e-invoice portal, then paste the IRN/ACK details here — they print at the top of the PDF.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input label="IRN No." placeholder="64-character IRN" maxLength={64} disabled={einvoiceLocked}
                  value={einvoice.irnNo} error={einvoiceErrors.irnNo}
                  onChange={(e) => { setEinvoice((p) => ({ ...p, irnNo: e.target.value.replace(/[^a-fA-F0-9]/g, '') })); setEinvoiceErrors((p) => ({ ...p, irnNo: undefined })); }} />
                <Input label="ACK No." placeholder="Digits only" maxLength={20} disabled={einvoiceLocked}
                  value={einvoice.ackNo} error={einvoiceErrors.ackNo}
                  onChange={(e) => { setEinvoice((p) => ({ ...p, ackNo: e.target.value.replace(/\D/g, '') })); setEinvoiceErrors((p) => ({ ...p, ackNo: undefined })); }} />
                <Input label="ACK Date" type="date" disabled={einvoiceLocked} min={invoice.invoiceDate} max={todayISO()}
                  value={einvoice.ackDate} error={einvoiceErrors.ackDate}
                  onChange={(e) => { setEinvoice((p) => ({ ...p, ackDate: e.target.value })); setEinvoiceErrors((p) => ({ ...p, ackDate: undefined })); }} />
              </div>
              {!einvoiceLocked && (
                <Button variant="outline" size="sm" className="mt-3" disabled={einvoiceMutation.isPending} onClick={saveEinvoice}>
                  {einvoiceMutation.isPending ? 'Saving…' : 'Save E-Invoice Details'}
                </Button>
              )}
            </Block>

            <p className="mt-10 text-center text-xs text-ink-400 tracking-wide">This is a computer-generated invoice.</p>
          </div>
        </div>

        {/* Record payment */}
        <Modal isOpen={showPayment} onClose={() => setShowPayment(false)} title="Record Payment" size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
              <Button variant="success" icon="fa-check" onClick={recordPayment} disabled={busy}>
                {busy ? 'Saving…' : 'Mark as Paid'}
              </Button>
            </div>
          }>
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              Invoice total <span className="font-bold text-ink-900">{fmtCurrency(total)}</span>
              {toNum(payment.tdsAmount) ? <> · amount received <span className="font-bold text-green-700">{fmtCurrency(total - (toNum(payment.tdsAmount) ?? 0))}</span></> : null}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Payment Date" type="date" required value={payment.paidAt} min={invoice.invoiceDate} max={todayISO()}
                error={paymentErrors.paidAt}
                onChange={(e) => { setPayment((p) => ({ ...p, paidAt: e.target.value })); setPaymentErrors((p) => ({ ...p, paidAt: undefined })); }} />
              <NativeSelect label="Payment Mode" required value={payment.paymentMode} error={paymentErrors.paymentMode}
                onChange={(val) => { setPayment((p) => ({ ...p, paymentMode: val })); setPaymentErrors((p) => ({ ...p, paymentMode: undefined })); }}
                options={PAYMENT_MODES} />
              <Input label="Reference / UTR" value={payment.paymentReference} maxLength={100} error={paymentErrors.paymentReference}
                onChange={(e) => { setPayment((p) => ({ ...p, paymentReference: e.target.value })); setPaymentErrors((p) => ({ ...p, paymentReference: undefined })); }} />
              <Input label="TDS Deducted (₹)" type="number" value={payment.tdsAmount} error={paymentErrors.tdsAmount}
                onChange={(e) => { setPayment((p) => ({ ...p, tdsAmount: e.target.value })); setPaymentErrors((p) => ({ ...p, tdsAmount: undefined })); }}
                helperText="If the client deducted TDS" />
            </div>
          </div>
        </Modal>

        {/* Cancel */}
        <Modal isOpen={showCancel} onClose={() => setShowCancel(false)} title="Cancel Invoice" size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCancel(false)}>Keep Invoice</Button>
              <Button variant="danger" onClick={cancelInvoice} disabled={busy}>{busy ? 'Cancelling…' : 'Cancel Invoice'}</Button>
            </div>
          }>
          <p className="text-sm text-ink-600 mb-4">
            Cancelling is permanent — the number <span className="font-mono font-semibold">{invoice.invoiceNumber}</span> stays
            used and the invoice is excluded from revenue. You can then generate a fresh one.
          </p>
          <Input label="Reason" required value={cancelReason} maxLength={255} error={cancelError}
            placeholder="Why is this invoice being cancelled?"
            onChange={(e) => { setCancelReason(e.target.value); setCancelError(''); }} />
        </Modal>
      </div>
    </Layout>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-widest mb-1">{children}</p>;
}

function Meta({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div>
      <Label>{label}</Label>
      <p className={`text-sm font-semibold ${danger ? 'text-red-600' : 'text-ink-900'}`}>{value}</p>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-8 pt-4 border-t border-ink-200">
      <Label>{title}</Label>
      {children}
    </div>
  );
}

function FootRow({ label, value, first }: { label: string; value: string; first?: boolean }) {
  return (
    <tr className={first ? 'border-t-2 border-ink-900' : ''}>
      <td colSpan={6} className={`px-3 ${first ? 'pt-4' : 'pt-1.5'} pb-1.5 text-right text-ink-600`}>{label}</td>
      <td className={`px-3 ${first ? 'pt-4' : 'pt-1.5'} pb-1.5 text-right font-mono text-ink-700`}>{value}</td>
    </tr>
  );
}
